import { z } from "zod"
import { ONBOARDING_STATUSES, SHOP_MEMBER_ROLES, USER_ROLES } from "@osmix/auth-policy"

/** Superset of every account role the gateway can return. Values are sourced
 *  from `@osmix/auth-policy` so the value list and this schema never drift. */
export const UserRoleSchema = z.enum(USER_ROLES)
export type UserRole = z.infer<typeof UserRoleSchema>

export const OnboardingStatusSchema = z.enum(ONBOARDING_STATUSES)
export type OnboardingStatus = z.infer<typeof OnboardingStatusSchema>

export const ShopMemberRoleSchema = z.enum(SHOP_MEMBER_ROLES)
export type ShopMemberRole = z.infer<typeof ShopMemberRoleSchema>

/** Admin-console platform-role metadata mixed onto the user for admin-console
 *  sessions; absent for seller/buyer sessions. */
export const PlatformRoleSummarySchema = z.object({
  key: z.string(),
  name: z.string(),
  description: z.string().optional(),
  system: z.boolean(),
  // Закрытые разделы консоли - с замком, а не скрыты (роль «Маркетолог», 09.10.2026).
  showLockedNav: z.boolean().optional(),
})
export type PlatformRoleSummary = z.infer<typeof PlatformRoleSummarySchema>

export const UserSchema = z.object({
  id: z.string(),
  email: z.string(),
  phone: z.string(),
  name: z.string(),
  shopName: z.string().optional(),
  /** Marketplace storefront this seller owns; absent for admins. */
  shopId: z.string().optional(),
  role: UserRoleSchema,
  onboardingStatus: OnboardingStatusSchema,
  avatar: z.string().optional(),
  /** Present only on admin-console sessions (mirrors the gateway user). */
  platformRole: PlatformRoleSummarySchema.optional(),
  effectivePermissions: z.array(z.string()).optional(),
})
export type User = z.infer<typeof UserSchema>

/** User-management row as returned to the admin console. */
export const UserRowSchema = UserSchema.extend({
  createdAt: z.string(),
  // Older admin payloads returned this as a loose string; keep migration lenient.
  onboardingStatus: z.union([OnboardingStatusSchema, z.string()]),
})
export type UserRow = z.infer<typeof UserRowSchema>

export const AuthSessionSchema = z.object({
  token: z.string(),
  user: UserSchema,
})
export type AuthSession = z.infer<typeof AuthSessionSchema>

export const LoginCredentialsSchema = z.object({
  email: z.string(),
  password: z.string(),
})
export type LoginCredentials = z.infer<typeof LoginCredentialsSchema>

export const LoginResponseSchema = z.union([
  z.object({
    requiresTwoFactor: z.literal(true),
    twoFactorMethod: z.enum(["sms", "email"]),
    maskedTarget: z.string(),
    sessionId: z.string(),
  }),
  z.object({
    requiresTwoFactor: z.literal(false),
    token: z.string(),
    user: UserSchema,
  }),
])
export type LoginResponse = z.infer<typeof LoginResponseSchema>

export const VerifyCodeRequestSchema = z.object({
  code: z.string(),
  sessionId: z.string(),
})
export type VerifyCodeRequest = z.infer<typeof VerifyCodeRequestSchema>

export const VerifyCodeResponseSchema = z.union([
  z.object({
    success: z.literal(true),
    token: z.string(),
    user: UserSchema,
  }),
  z.object({
    success: z.literal(false),
    attemptsLeft: z.number().int().nonnegative(),
    fallback: z.enum(["phone_call", "restore_access", "account_deactivated"]).optional(),
  }),
])
export type VerifyCodeResponse = z.infer<typeof VerifyCodeResponseSchema>

export const RegisterRequestSchema = z.object({
  email: z.string(),
  /** Canonical E.164, e.g. "+79991234567". */
  phone: z.string(),
})
export type RegisterRequest = z.infer<typeof RegisterRequestSchema>

export const RegisterResponseSchema = z.object({
  sessionId: z.string(),
  emailSent: z.boolean(),
})
export type RegisterResponse = z.infer<typeof RegisterResponseSchema>

export const VerifyEmailRequestSchema = VerifyCodeRequestSchema
export type VerifyEmailRequest = VerifyCodeRequest

export const VerifySmsRequestSchema = VerifyCodeRequestSchema
export type VerifySmsRequest = VerifyCodeRequest

export const CreatePasswordRequestSchema = z.object({
  password: z.string(),
  sessionId: z.string(),
})
export type CreatePasswordRequest = z.infer<typeof CreatePasswordRequestSchema>

export const CreatePasswordResponseSchema = AuthSessionSchema
export type CreatePasswordResponse = z.infer<typeof CreatePasswordResponseSchema>

export const AdminLoginRequestSchema = LoginCredentialsSchema
export type AdminLoginRequest = LoginCredentials

export const AdminLoginResponseSchema = AuthSessionSchema
export type AdminLoginResponse = z.infer<typeof AdminLoginResponseSchema>

export const SurveyDataSchema = z.object({
  businessForm: z.enum(["legal_entity", "self_employed", "individual_entrepreneur"]),
  role: z.string(),
  categories: z.array(z.string()),
  salesChannels: z.array(z.string()),
  contact: z.object({
    name: z.string(),
    city: z.string(),
    phone: z.string(),
    preferredContact: z.string(),
    preferredTimeSlots: z.array(z.string()),
  }),
})
export type SurveyData = z.infer<typeof SurveyDataSchema>

export const RestoreAccessRequestSchema = z.object({
  email: z.string(),
  phone: z.string(),
  description: z.string(),
  /** Browser-only attachments; transport is form-data, so keep the value opaque. */
  photos: z.array(z.unknown()).optional(),
})
export type RestoreAccessRequest = z.infer<typeof RestoreAccessRequestSchema>

export const UserPatchSchema = z.object({
  name: z.string().optional(),
  phone: z.string().optional(),
  shopName: z.string().optional(),
  role: UserRoleSchema.optional(),
  onboardingStatus: OnboardingStatusSchema.optional(),
})
export type UserPatch = z.infer<typeof UserPatchSchema>
